import {Component, computed, inject, Input, input, output, signal} from '@angular/core';
import {FormValidationClassDirective} from '@core/directives/form-validation-class.directive';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {UserService} from '@core/services/user.service';
import {CustomerProfile} from '@core/interfaces/generated/graphql';
import {TrackEventService} from '@core/services/track-event.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {SuperpayService} from '@core/services/superpay.service';
import {CheckBadgeIconComponent} from '@core/svg-icons/check-badge-icon.component';
import {FingerPrintIconComponent} from '@core/svg-icons/finger-print-icon.component';
import {CartService} from '@core/services/cart.service';

@Component({
  selector: 'app-login-signup-form',
  templateUrl: './login-signup-form.component.html',
  styleUrls: ['./login-signup-form.component.scss'],
  imports: [
    FormValidationClassDirective,
    ReactiveFormsModule,
    CheckBadgeIconComponent,
    FingerPrintIconComponent,
  ]
})

export class LoginSignupFormComponent {
  private userService = inject(UserService);
  private trackEventService = inject(TrackEventService);
  private modalService = inject(NgbModal);
  private superpayService = inject(SuperpayService);
  private cartService = inject(CartService);

  submittingLogin = signal(false);
  loginFailed = signal(false);
  emailSubmitted = signal(false);
  customerHasPasskey = signal(false);

  loginForm = new FormGroup<{
    username: FormControl<string | null>,
    password: FormControl<string | null>
  }>({
    username: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required])
  });

  showContinueAsGuest = input(false);

  readonly continueAsGuest = output();

  readonly loginSuccess = output<CustomerProfile | undefined>();

  @Input() set email(value: string | undefined) {
    if (!value) {
      return;
    }
    this.loginForm.controls.username.setValue(value);
  }

  submitButtonText = computed(() => {
    if (this.emailSubmitted()) {
      return $localize`LOGIN`;
    }
    return $localize`FORTSÆT`;
  });

  forgottenPasswordModal() {
    this.userService.forgottenPasswordSendEmailModal(this.loginForm.controls.username.value);
    this.trackEventService.trackClickEvent('forgotten_password_modal_open');
  }

  createAccountModal() {
    this.modalService.dismissAll();
    import('@layout/components/modals/create-account-modal/create-account-modal.component').then(({CreateAccountModalComponent}) => {
      const modalRef = this.modalService.open(CreateAccountModalComponent);
      const email = this.loginForm.controls.username.value;
      if (email) {
        modalRef.componentInstance.email = email;
      }
      this.trackEventService.trackClickEvent('create_account_modal_open');
    });
  }

  async submitLogin() {
    if (this.loginForm.controls.username.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    if (this.loginForm.controls.password.invalid || !this.emailSubmitted()) {
      await this.usePasskeyLogin();
      this.emailSubmitted.set(true);
      return;
    }
    if (!this.loginForm.value.username || !this.loginForm.value.password) {
      return;
    }
    this.submittingLogin.set(true);
    this.loginFailed.set(false);
    let user;
    try {
      user = await this.userService.login(this.loginForm.value.username, this.loginForm.value.password);
    } catch (error) {
    }

    this.submittingLogin.set(false);

    if (!user) {
      this.loginFailed.set(true);
      return;
    }
    this.loginSuccess.emit(user);
  }

  async usePasskeyLogin() {
    try {
      this.submittingLogin.set(true);
      const email = this.loginForm.controls.username.value;
      const cartId = this.cartService.cartSignal().cart.cartId;
      const uid = undefined;
      if (email) {
        this.customerHasPasskey.set(false);
        const result = await this.superpayService.superpayIsPasskeyCredentialsAvailable(uid, email);
        console.log('superpayIsPasskeyCredentialsAvailable', result);
        if (result) {
          this.customerHasPasskey.set(true);
          const passkeyLogin = await this.superpayService.superpayPromptPasskeyLoginIfAvailable(uid, email, cartId);
          console.log('superpayPromptPasskeyLoginIfAvailable', passkeyLogin);
          if (passkeyLogin?.success === true) {
            await this.userService.updateSessionAfterLoginWithPasskey(passkeyLogin.customerToken, passkeyLogin.sourceCartId);
            this.loginSuccess.emit(undefined);
          }
          this.submittingLogin.set(false);
          return;
        }
        this.submittingLogin.set(false);
        return;
      }
    } catch (error) {
      this.submittingLogin.set(false);
      console.error('usePasskeyLogin', error);
    }
  }
}
